import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, MapPin, Phone, Plus, Pencil, Trash2, ExternalLink, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Field, EmptyState } from "@/components/Field";

const blank = () => ({ name: "", address: "", cap: "", city: "", phone: "", email: "", maps_url: "", directions: "", external: false });

const StudioDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(blank());
  useEffect(() => { if (open) setF(initial ? { ...blank(), ...initial } : blank()); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = Object.fromEntries(Object.keys(blank()).map((k) => [k, k === "external" ? !!f[k] : f[k] || ""]));
      initial ? await api.put(`/studios/${initial.id}`, body) : await api.post("/studios", body);
      toast.success("Studio salvato");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const inp = (k, label, props = {}) => (
    <Field label={label}><input className="field" value={f[k]} onChange={set(k)} data-testid={`studio-field-${k}`} {...props} /></Field>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-2xl" data-testid="studio-dialog">
        <DialogHeader><DialogTitle>{initial ? "Modifica studio" : "Nuovo studio"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {inp("name", "Nome studio", { required: true, placeholder: "Es. Studio Torino Centro" })}
          <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#edf2ef] p-1" data-testid="studio-type-toggle">
            {[[false, "Mio studio"], [true, "Studio esterno (nessun incasso)"]].map(([v, l]) => (
              <button type="button" key={l} onClick={() => setF({ ...f, external: v })} data-testid={`studio-type-${v ? "external" : "own"}`}
                className={`rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${!!f.external === v ? "bg-white text-[#3d655b] shadow-sm" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
          {inp("address", "Indirizzo", { placeholder: "Via e numero civico" })}
          <div className="grid grid-cols-3 gap-3">
            {inp("cap", "CAP")}
            <div className="col-span-2">{inp("city", "Città")}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {inp("phone", "Telefono", { type: "tel" })}
            {inp("email", "Email", { type: "email" })}
          </div>
          {inp("maps_url", "Link Google Maps (facoltativo)", { type: "url", placeholder: "https://www.google.com/maps/place/..." })}
          <p className="-mt-2 text-xs text-slate-500">Se lasci vuoto, il link alla mappa viene creato in automatico dall'indirizzo. I link brevi (maps.app.goo.gl) non sono accettati nelle email: usa il link completo.</p>
          <Field label="Indicazioni per il paziente (facoltativo)">
            <textarea className="field min-h-[60px]" value={f.directions} onChange={set("directions")} placeholder="Es. 2° piano, citofono 'Studio Medico', parcheggio interno" data-testid="studio-field-directions" />
          </Field>
          <div className="flex justify-end"><button className="btn-primary" data-testid="studio-save-button">Salva studio</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const StudioCard = ({ s, onEdit, onDelete }) => (
  <div className="card flex flex-col gap-4" data-testid={`studio-card-${s.id}`}>
    <div className="flex items-start gap-3">
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#edf2ef] text-[#3d655b]"><Building2 className="h-5 w-5" /></div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-lg font-bold">{s.name}{s.external && <span className="rounded-full bg-[#fbf8f4] px-2 py-0.5 text-[11px] font-semibold text-[#8a6d4f] ring-1 ring-[#e3d3bf]" data-testid={`studio-external-badge-${s.id}`}>Esterno · nessun incasso</span>}</div>
        {(s.address || s.city) && <div className="flex items-start gap-1 text-sm text-slate-600"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />{[s.address, [s.cap, s.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>}
        {s.phone && <div className="flex items-center gap-1 text-sm text-slate-600"><Phone className="h-3.5 w-3.5" />{s.phone}</div>}
        {s.directions && <p className="mt-1 text-xs text-slate-500">{s.directions}</p>}
      </div>
    </div>
    <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-[#e2e8f0] pt-3">
      <span className="flex items-center gap-1 text-xs font-semibold text-slate-500" data-testid={`studio-patients-${s.id}`}><Users className="h-3.5 w-3.5" />{s.patients_count} pazienti</span>
      <div className="flex gap-1">
        <a href={s.maps_link} target="_blank" rel="noreferrer" className="btn-ghost text-[#b8956f]" data-testid={`studio-maps-${s.id}`}><ExternalLink className="h-4 w-4" /> Mappa</a>
        <button className="btn-ghost" onClick={() => onEdit(s)} data-testid={`studio-edit-${s.id}`}><Pencil className="h-4 w-4" /></button>
        <button className="btn-ghost hover:text-rose-600" onClick={() => onDelete(s)} data-testid={`studio-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  </div>
);

export default function Studios() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/studios").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Eliminare "${s.name}"? I pazienti assegnati resteranno senza studio.`)) return;
    await api.delete(`/studios/${s.id}`);
    toast.success("Studio eliminato");
    load();
  };

  return (
    <div className="fade-up space-y-8" data-testid="studios-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-xs text-[#3d655b]">Anagrafica sedi</div>
          <h1 className="page-title mt-1">Studi</h1>
          <p className="mt-1 text-sm text-slate-500">Assegna uno studio a ogni paziente: le email degli appuntamenti includeranno indirizzo e mappa dello studio.</p>
        </div>
        <button className="btn-primary" onClick={() => { setEdit(null); setOpen(true); }} data-testid="new-studio-button"><Plus className="h-4 w-4" /> Nuovo studio</button>
      </div>
      {list && list.length === 0 ? (
        <EmptyState icon={Building2} title="Nessuno studio inserito" text="Aggiungi i tuoi studi con indirizzo per inviare la posizione ai pazienti." testid="studios-empty" />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {(list || []).map((s) => <StudioCard key={s.id} s={s} onEdit={(x) => { setEdit(x); setOpen(true); }} onDelete={remove} />)}
        </div>
      )}
      <StudioDialog open={open} onOpenChange={setOpen} initial={edit} onSaved={load} />
    </div>
  );
}
